// @ts-strict-ignore
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { useMemo } from "react";
import type { IProjectGroupElement } from "src/model/model";
import type { IMedia } from "src/model/unityObject";
import { mediaSelector } from "src/features/gallery/state/gallerySlice";
import { getCurrentElements } from "src/features/projectGroups/state/projectGroupsSlice";
import TitleWithTooltip from "src/common/components/TitleWithTooltip/TitleWithTooltip";
import ProjectCard from "../ProjectCard";

export const ModulesSelectedForPublication = ({
  selectedProjectIds = [],
}: {
  selectedProjectIds: Array<number | string>;
}) => {
  const { t } = useTranslation();
  const currentElements = useAppSelector(getCurrentElements);
  const medias = useAppSelector(mediaSelector);

  const selectedIdSet = useMemo(
    () => new Set(selectedProjectIds.map((id) => Number(id))),
    [selectedProjectIds],
  );

  const selectedCards = useMemo(
    () =>
      currentElements
        .filter((pge: IProjectGroupElement) => selectedIdSet.has(Number(pge.project.id)))
        .map((pge: IProjectGroupElement) => {
          const sourceTbn = pge.project.source_thumbnail as any;
          const mediaId = sourceTbn?.id ? Number(sourceTbn.id) : Number(sourceTbn);
          const s3Url = medias.find((m: IMedia) => Number(m.id) === mediaId)?.s3_url as
            | string
            | undefined;

          return {
            id: String(pge.project.id),
            name: pge.project.name,
            s3Url: s3Url || "",
          };
        }),
    [currentElements, medias, selectedIdSet],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <TitleWithTooltip
          title={t("pages.projectGroup.publish.confirmationBeforePublish.selectedModules")}
          tooltip={t("tooltip.modulesToBePublishedExplanation")}
          className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
        />
        <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
          {selectedCards.length}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 overflow-y-auto min-h-0 pr-0.5">
        {selectedCards.map((card) => (
          <div key={card.id} className="rounded-xl border-2 border-emerald-400">
            <ProjectCard s3Url={card.s3Url} projectName={card.name} disableHover disabled />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModulesSelectedForPublication;
